import { useEffect, useState } from "react";

import {
  fetchRegionalMeasurement,
  fetchRegionalMeasurementSignals,
} from "../../utils/backend/pdxBackendClient";

const RESULT_POLL_MS = 4000;
const DEFAULT_SIGNAL_ALGORITHM = "mpm";

function isNotFound(error) {
  return (
    error?.status === 404 ||
    error?.body?.error?.code === "MEASUREMENT_NOT_FOUND"
  );
}

function buildSignalProcessingFieldName(signalAlgorithm = DEFAULT_SIGNAL_ALGORITHM) {
  const normalizedAlgorithm =
    typeof signalAlgorithm === "string" && signalAlgorithm.trim().length > 0
      ? signalAlgorithm.trim().toLowerCase()
      : DEFAULT_SIGNAL_ALGORITHM;

  return `${normalizedAlgorithm}-signal-processed`;
}

function stripLegacySignalProcessing(measurement) {
  if (!measurement || typeof measurement !== "object") {
    return measurement;
  }

  const sanitizedMeasurement = { ...measurement };

  delete sanitizedMeasurement["mpm-signal-processed"];
  delete sanitizedMeasurement["pca-signal-processed"];
  delete sanitizedMeasurement.mpmSignalProcessed;
  delete sanitizedMeasurement.mpm_signal_processed;
  delete sanitizedMeasurement.pcaSignalProcessed;
  delete sanitizedMeasurement.pca_signal_processed;

  return sanitizedMeasurement;
}

function mergeSignalProcessing(measurement, signalResponse) {
  const sanitizedMeasurement = stripLegacySignalProcessing(measurement);

  if (!sanitizedMeasurement || typeof sanitizedMeasurement !== "object") {
    return sanitizedMeasurement;
  }

  const signalProcessing =
    signalResponse?.signalProcessing &&
    typeof signalResponse.signalProcessing === "object"
      ? signalResponse.signalProcessing
      : null;

  if (!signalProcessing) {
    return sanitizedMeasurement;
  }

  return {
    ...sanitizedMeasurement,
    [buildSignalProcessingFieldName(signalResponse?.signalAlgorithm)]:
      signalProcessing,
  };
}

/**
 * Polls a single measurement result document through measurement-service and
 * hydrates its signal traces through results-service.
 *
 * Measurement data comes from `/v1/app/measurements/:variant/:measurementId`.
 */
export function useMeasurementResultDoc(uid, variant, docId) {
  const [data, setData] = useState(null);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(!!uid && !!variant && !!docId);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState({
    fromCache: false,
    hasPendingWrites: false,
  });

  useEffect(() => {
    if (!uid || !variant || !docId) {
      setData(null);
      setExists(false);
      setLoading(false);
      setError(null);
      setMetadata({ fromCache: false, hasPendingWrites: false });
      return undefined;
    }

    let cancelled = false;
    let timeoutId = null;

    const poll = async (isInitial = false) => {
      if (isInitial) {
        setLoading(true);
      }
      setError(null);
      setMetadata({ fromCache: false, hasPendingWrites: false });

      try {
        const response = await fetchRegionalMeasurement({
          variant,
          measurementId: docId,
        });

        if (cancelled) return;

        const measurement = response?.measurement || null;
        let mergedMeasurement = stripLegacySignalProcessing(measurement);

        if (measurement) {
          try {
            const signalResponse = await fetchRegionalMeasurementSignals({
              variant,
              measurementId: docId,
              signalAlgorithm: DEFAULT_SIGNAL_ALGORITHM,
            });

            if (cancelled) return;
            mergedMeasurement = mergeSignalProcessing(
              measurement,
              signalResponse,
            );
          } catch {
            if (cancelled) return;
          }
        }

        setData(mergedMeasurement);
        setExists(Boolean(measurement));
        setLoading(false);
      } catch (fetchError) {
        if (cancelled) return;

        if (isNotFound(fetchError)) {
          setData(null);
          setExists(false);
          setError(null);
        } else {
          setError(fetchError);
        }
        setLoading(false);
      }

      if (!cancelled) {
        timeoutId = setTimeout(() => poll(false), RESULT_POLL_MS);
      }
    };

    poll(true);

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [docId, uid, variant]);

  return { data, exists, loading, error, metadata };
}
